import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export type SignPdfResourceType = "prescription" | "prontuario_export" | "patient_term";

export interface SignPdfPayload {
  resource_type: SignPdfResourceType;
  resource_id: string;
  user_timezone?: string;
  /** Used for query invalidation after prescription signing */
  pet_id?: string;
  appointment_id?: string;
  medical_record_id?: string;
  /** Used for query invalidation after prontuario_export signing */
  prontuario_id?: string;
  /** Used for query invalidation after patient_term signing */
  term_pet_id?: string;
}

export interface SignPdfResult {
  success: boolean;
  signed_pdf_base64: string;
  sha256: string;
  storage_path: string;
  access_code?: string;
  secret_code?: string;
  public_url?: string;
  signed_pdf_url?: string;
}

export const useSignPdf = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["prescriptions", "medicalRecords"]);

  return useMutation({
    mutationFn: async (payload: SignPdfPayload): Promise<SignPdfResult> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("unauthenticated");

      // Signing runs exclusively in the PDF Lambda (services/pdf-lambda). The legacy
      // sign-pdf edge function was removed; VITE_PDF_LAMBDA_URL must be configured.
      const lambdaUrl = import.meta.env.VITE_PDF_LAMBDA_URL as string | undefined;
      if (!lambdaUrl) throw new Error("pdf_lambda_url_not_configured");

      const resp = await fetch(`${lambdaUrl.replace(/\/$/, "")}/sign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          resource_type: payload.resource_type,
          resource_id: payload.resource_id,
          user_timezone: payload.user_timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const json = await resp.json();
      if (!resp.ok || !json?.success) throw new Error(json?.error ?? "unknown_error");
      return json as SignPdfResult;
    },
    onSuccess: (_, variables) => {
      if (variables.resource_type === "prescription") {
        queryClient.invalidateQueries({ queryKey: ["medical-prescriptions", variables.pet_id] });
        if (variables.appointment_id) {
          queryClient.invalidateQueries({ queryKey: ["medical-prescriptions", undefined, variables.appointment_id] });
        }
        if (variables.medical_record_id) {
          queryClient.invalidateQueries({ queryKey: ["medical-prescriptions", undefined, undefined, variables.medical_record_id] });
        }
        queryClient.invalidateQueries({ queryKey: ["medical-prescriptions"] });
        queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
      } else if (variables.resource_type === "patient_term") {
        queryClient.invalidateQueries({ queryKey: ["patient-terms"] });
        queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
        if (variables.term_pet_id) {
          queryClient.invalidateQueries({ queryKey: ["patient-terms", variables.term_pet_id] });
        }
      } else {
        if (variables.prontuario_id) {
          queryClient.invalidateQueries({ queryKey: ["prontuario-timeline", variables.prontuario_id] });
        }
        queryClient.invalidateQueries({ queryKey: ["prontuario-exports"] });
      }
    },
    onError: (error: Error) => {
      const key = error.message as string;
      const i18nKey = `prescriptions:sign.error.${key}`;
      const translated = t(i18nKey, { defaultValue: "" });
      const message = translated || t("prescriptions:sign.errorGeneric", { defaultValue: "Erro ao assinar documento" });
      toast.error(message);
    },
  });
};
